import { useEffect, useRef, useState } from 'react';
import Soundfont from 'soundfont-player';

// Lazy-load a General-MIDI instrument via soundfont-player. Samples
// come from the gleitz/midi-js-soundfonts CDN (FluidR3 by default),
// ~200 KB per instrument, cached by the browser after first load.
//
// Usage:
//   const { instrument, ready, error } = useSoundfont('acoustic_guitar_nylon', audioContext);
//   instrument?.play(60); // MIDI note number, or 'C4'
//
// The audioContext must be created and resumed by a user gesture
// before the instrument can play any sound.

const DEFAULT_SOUNDFONT = 'FluidR3_GM';

const cache = new Map(); // instrumentName -> Promise<player>

const loadInstrument = (ctx, name, soundfont = DEFAULT_SOUNDFONT) => {
  const key = `${soundfont}::${name}`;
  if (cache.has(key)) return cache.get(key);
  const p = Soundfont.instrument(ctx, name, { soundfont, format: 'mp3' });
  cache.set(key, p);
  return p;
};

export const useSoundfont = (instrumentName, audioContext, options = {}) => {
  const { soundfont = DEFAULT_SOUNDFONT, enabled = true } = options;
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const instrumentRef = useRef(null);

  useEffect(() => {
    if (!enabled || !audioContext || !instrumentName) return undefined;
    let cancelled = false;
    setReady(false);
    setError(null);
    loadInstrument(audioContext, instrumentName, soundfont)
      .then(player => {
        if (cancelled) return;
        instrumentRef.current = player;
        setReady(true);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err);
        instrumentRef.current = null;
      });
    return () => { cancelled = true; };
  }, [audioContext, instrumentName, soundfont, enabled]);

  return { instrument: instrumentRef.current, ready, error };
};

// Convert (stringIndex 0=low E, fret) -> MIDI note number.
// Standard tuning: E2=40, A2=45, D3=50, G3=55, B3=59, E4=64.
const OPEN_MIDI = [40, 45, 50, 55, 59, 64];
export const fretToMidi = (stringIndex, fret) => {
  if (fret < 0 || stringIndex < 0 || stringIndex > 5) return null;
  return OPEN_MIDI[stringIndex] + fret;
};

export default useSoundfont;
